import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import BoardPreview from "../components/BoardPreview";
import { DEFAULT_STORYBOARD_TEMPLATE } from "../constants/storyboardTemplates";
import AdUnit from "../components/AdUnit";

const MOCKUP_W = 240;
const MOCKUP_H = 320;
const PAD = 12;
const GAP = 8;
const INNER_W = MOCKUP_W - 2 * PAD;
const INNER_H = MOCKUP_H - 2 * PAD;
const MIN_PANEL = 20;
const PANEL_COLORS = ["#1a1a1a", "#00a4e4", "#333", "#e4007c", "#1a1a1a", "#ffe600"];

const clampVal = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

const getDefaultSplits = () => {
  const colW = (INNER_W - 2 * GAP) / 3;
  const rowH = (INNER_H - 2 * GAP) / 3;
  return {
    splitY1: rowH,
    splitY2: rowH * 2 + GAP,
    splitXRow1: colW * 2 + GAP,
    splitXRows23: colW,
    splitXRow3: colW + GAP + colW,
  };
};

const InteractiveMockup = () => {
  const [splits, setSplits] = useState(getDefaultSplits);
  const [dragging, setDragging] = useState(null);
  const innerRef = useRef(null);

  const getPanelRects = useCallback(() => {
    const { splitY1, splitY2, splitXRow1, splitXRows23, splitXRow3 } = splits;
    return [
      { x: 0, y: 0, w: splitXRow1, h: splitY1, color: PANEL_COLORS[0] },
      { x: splitXRow1 + GAP, y: 0, w: INNER_W - splitXRow1 - GAP, h: splitY1, color: PANEL_COLORS[1] },
      { x: 0, y: splitY1 + GAP, w: splitXRows23, h: INNER_H - splitY1 - GAP, color: PANEL_COLORS[2] },
      { x: splitXRows23 + GAP, y: splitY1 + GAP, w: INNER_W - splitXRows23 - GAP, h: splitY2 - splitY1 - GAP, color: PANEL_COLORS[3] },
      { x: splitXRows23 + GAP, y: splitY2 + GAP, w: splitXRow3 - splitXRows23 - GAP, h: INNER_H - splitY2 - GAP, color: PANEL_COLORS[4] },
      { x: splitXRow3 + GAP, y: splitY2 + GAP, w: INNER_W - splitXRow3 - GAP, h: INNER_H - splitY2 - GAP, color: PANEL_COLORS[5] },
    ];
  }, [splits]);

  const startDrag = (splitKey, event) => {
    event.preventDefault();
    const rect = innerRef.current.getBoundingClientRect();
    setDragging({ key: splitKey, originX: rect.left, originY: rect.top });
  };

  useEffect(() => {
    if (!dragging) return;

    const handleMove = (e) => {
      const mx = e.clientX - dragging.originX;
      const my = e.clientY - dragging.originY;

      setSplits((prev) => {
        const next = { ...prev };
        switch (dragging.key) {
          case "splitY1":
            next.splitY1 = clampVal(my, MIN_PANEL, next.splitY2 - GAP - MIN_PANEL);
            break;
          case "splitY2":
            next.splitY2 = clampVal(my, next.splitY1 + GAP + MIN_PANEL, INNER_H - MIN_PANEL);
            break;
          case "splitXRow1":
            next.splitXRow1 = clampVal(mx, MIN_PANEL, INNER_W - GAP - MIN_PANEL);
            break;
          case "splitXRows23":
            next.splitXRows23 = clampVal(mx, MIN_PANEL, Math.min(next.splitXRow3 - GAP - MIN_PANEL, INNER_W - GAP - MIN_PANEL));
            break;
          case "splitXRow3":
            next.splitXRow3 = clampVal(mx, next.splitXRows23 + GAP + MIN_PANEL, INNER_W - GAP - MIN_PANEL);
            break;
          default:
            break;
        }
        return next;
      });
    };

    const handleUp = () => setDragging(null);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging]);

  const panels = getPanelRects();
  const { splitY1, splitY2, splitXRow1, splitXRows23, splitXRow3 } = splits;
  const handleThickness = 12;

  const handles = [
    { key: "splitY1", cursor: "ns-resize", x: 0, y: splitY1, w: INNER_W, h: GAP },
    { key: "splitY2", cursor: "ns-resize", x: splitXRows23 + GAP, y: splitY2, w: INNER_W - splitXRows23 - GAP, h: GAP },
    { key: "splitXRow1", cursor: "ew-resize", x: splitXRow1, y: 0, w: GAP, h: splitY1 },
    { key: "splitXRows23", cursor: "ew-resize", x: splitXRows23, y: splitY1 + GAP, w: GAP, h: INNER_H - splitY1 - GAP },
    { key: "splitXRow3", cursor: "ew-resize", x: splitXRow3, y: splitY2 + GAP, w: GAP, h: INNER_H - splitY2 - GAP },
  ];

  return (
    <div style={styles.heroPreview}>
      <div style={styles.mockupPage}>
        <div ref={innerRef} style={{ position: "relative", width: INNER_W, height: INNER_H }}>
          {panels.map((p, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                left: p.x,
                top: p.y,
                width: Math.max(0, p.w),
                height: Math.max(0, p.h),
                background: p.color,
              }}
            />
          ))}
          {handles.map((h) => (
            <div
              key={h.key}
              onMouseDown={(e) => startDrag(h.key, e)}
              style={{
                position: "absolute",
                left: h.x - (handleThickness - h.w) / 2,
                top: h.y - (handleThickness - h.h) / 2,
                width: h.cursor === "ew-resize" ? handleThickness : h.w,
                height: h.cursor === "ns-resize" ? handleThickness : h.h,
                cursor: h.cursor,
                zIndex: 2,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const LandingPage = () => {
  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <header style={styles.hero}>
        {/* Halftone dot overlay */}
        <div style={styles.halftoneOverlay} />
        <div style={styles.heroInner}>
          <div style={styles.heroContent}>
            <h1 style={styles.logo}>striply</h1>
            <p style={styles.tagline}>
              Create beautiful comic page layouts in minutes
            </p>
            <p style={styles.subtitle}>
              The fast, simple way to design professional comic panels.
              No complex software. No learning curve. Just drag, resize, and export.
            </p>
            <Link to="/editor" style={styles.ctaButton}>
              Start Creating
            </Link>
          </div>

          <InteractiveMockup />
        </div>
      </header>

      {/* Features Section */}
      <section style={styles.features}>
        <h2 style={styles.sectionTitle}>Why Creators Love Striply</h2>

        <div style={styles.featureGrid}>
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>+</div>
            <h3 style={styles.featureTitle}>Intuitive Panel Resizing</h3>
            <p style={styles.featureDesc}>
              Drag any edge or corner to resize. Adjacent panels adjust automatically
              while maintaining perfect gutters.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>A</div>
            <h3 style={styles.featureTitle}>Speech Bubbles</h3>
            <p style={styles.featureDesc}>
              Add dialogue with draggable speech bubbles. Choose from square, rounded,
              or cloud shapes with customizable tails.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>[]</div>
            <h3 style={styles.featureTitle}>Print-Ready Export</h3>
            <p style={styles.featureDesc}>
              Export high-resolution PDFs sized for standard letter pages (8.5" x 11").
              Perfect for printing or sharing.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>#</div>
            <h3 style={styles.featureTitle}>Layout Templates</h3>
            <p style={styles.featureDesc}>
              Start with pre-built templates: 3x3 grid, 2x2 grid, horizontal strips,
              or vertical strips. Customize from there.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>&#x1F5BC;</div>
            <h3 style={styles.featureTitle}>Background Images</h3>
            <p style={styles.featureDesc}>
              Upload your artwork directly into any panel. Images scale to fit
              and persist across sessions so your work is never lost.
            </p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>&#x21C5;</div>
            <h3 style={styles.featureTitle}>Save & Share</h3>
            <p style={styles.featureDesc}>
              Your layout auto-saves as you work. Export as JSON to back up
              or share with collaborators, then import to pick up where you left off.
            </p>
          </div>
        </div>
      </section>

      {/* Ad between Features and How It Works */}
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 40px" }}>
        <AdUnit adSlot="8315041348" adFormat="auto" />
      </div>

      {/* How It Works Section */}
      <section style={styles.howItWorks}>
        <h2 style={styles.sectionTitle}>Create Your First Page in 3 Steps</h2>

        <div style={styles.steps}>
          <div style={styles.step}>
            <div style={styles.stepNumber}>1</div>
            <h3 style={styles.stepTitle}>Choose a Layout</h3>
            <p style={styles.stepDesc}>
              Pick from our templates or start with a blank grid.
              Portrait orientation fits standard comic pages.
            </p>
          </div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>2</div>
            <h3 style={styles.stepTitle}>Customize Panels</h3>
            <p style={styles.stepDesc}>
              Drag edges to resize panels. Add background images and
              speech bubbles to bring your story to life.
            </p>
          </div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>3</div>
            <h3 style={styles.stepTitle}>Export & Print</h3>
            <p style={styles.stepDesc}>
              Download your layout as a high-quality PDF.
              Print at home or send to your favorite printer.
            </p>
          </div>
        </div>

        <div style={styles.ctaSection}>
          <Link to="/editor" style={styles.ctaButtonSecondary}>
            Try the Editor Now
          </Link>
          <p style={styles.ctaNote}>Free to use. No account required.</p>
        </div>
      </section>

      {/* Storyboards: the second tool, and the door into its content cluster */}
      <section style={styles.storyboards}>
        <div style={styles.storyboardsInner}>
          <div style={styles.storyboardsText}>
            <h2 style={{ ...styles.sectionTitle, textAlign: "left", marginBottom: 16, fontSize: 30 }}>
              Storyboards, too
            </h2>
            <p style={styles.storyboardsDesc}>
              Planning a film, a YouTube video, or a Reel? Striply's{" "}
              <Link to="/storyboard" style={styles.storyboardsLink}>free storyboard maker</Link>{" "}
              locks every frame to your aspect ratio — 16:9, 2.39:1, or 9:16 —
              adds letterbox bars and vertical crop guides, and gives each shot
              its own notes for camera moves, timing, and dialogue.
            </p>
            <p style={styles.storyboardsDesc}>
              Start from a{" "}
              <Link to="/storyboard-templates" style={styles.storyboardsLink}>storyboard template</Link>,
              or learn the basics with{" "}
              <Link to="/guides/what-is-a-storyboard" style={styles.storyboardsLink}>what a storyboard is and how to make one</Link>.
            </p>
            <Link to="/storyboard" style={styles.ctaButtonSecondary}>Make a storyboard</Link>
          </div>
          <div style={styles.storyboardsPreview}>
            <BoardPreview
              seed={DEFAULT_STORYBOARD_TEMPLATE.seed}
              title="A blank 16:9 storyboard page"
              style={{ border: "2px solid #1a1a1a", boxShadow: "6px 6px 0 rgba(0,0,0,0.18)" }}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerBrand}>
            <span style={styles.footerLogo}>striply</span>
            <p style={styles.footerTagline}>Simple comic layout design</p>
          </div>
          <nav style={styles.footerNav}>
            <Link to="/editor" style={styles.footerLink}>Editor</Link>
            <Link to="/storyboard" style={styles.footerLink}>Storyboards</Link>
            <Link to="/collage" style={styles.footerLink}>Collage</Link>
            <Link to="/about" style={styles.footerLink}>About</Link>
            <Link to="/tutorial" style={styles.footerLink}>Tutorial</Link>
            <Link to="/guides" style={styles.footerLink}>Guides</Link>
            <Link to="/privacy" style={styles.footerLink}>Privacy</Link>
            <Link to="/terms" style={styles.footerLink}>Terms</Link>
          </nav>
        </div>
        <p style={styles.copyright}>
          Made for comic creators everywhere.
        </p>
      </footer>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#1a1a1a",
    lineHeight: 1.6,
  },

  // Hero
  hero: {
    position: "relative",
    overflow: "hidden",
    background: "#1a1a1a",
    color: "#f5f0e8",
    minHeight: "70vh",
  },
  halftoneOverlay: {
    position: "absolute",
    inset: 0,
    backgroundImage: "radial-gradient(circle, rgba(0,164,228,0.15) 1.5px, transparent 1.5px), radial-gradient(circle, rgba(228,0,124,0.1) 1px, transparent 1px)",
    backgroundSize: "12px 12px, 8px 8px",
    backgroundPosition: "0 0, 4px 4px",
    pointerEvents: "none",
    zIndex: 1,
  },
  heroInner: {
    position: "relative",
    zIndex: 2,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 60,
    padding: "80px 40px",
    flexWrap: "wrap",
  },
  heroContent: {
    maxWidth: 500,
    textAlign: "left",
  },
  logo: {
    fontSize: 56,
    fontWeight: 700,
    margin: 0,
    letterSpacing: -1,
    fontFamily: "'Georgia', serif",
    textTransform: "lowercase",
  },
  tagline: {
    fontSize: 28,
    fontWeight: 400,
    margin: "16px 0",
    opacity: 0.95,
    fontStyle: "italic",
  },
  subtitle: {
    fontSize: 18,
    opacity: 0.8,
    marginBottom: 32,
    lineHeight: 1.7,
  },
  ctaButton: {
    display: "inline-block",
    padding: "16px 40px",
    background: "#ffe600",
    color: "#1a1a1a",
    fontSize: 18,
    fontWeight: 700,
    borderRadius: 0,
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "3px solid #1a1a1a",
    fontFamily: "'Georgia', serif",
  },

  // Hero Preview (portrait mockup)
  heroPreview: {
    display: "flex",
    justifyContent: "center",
  },
  mockupPage: {
    width: MOCKUP_W,
    height: MOCKUP_H,
    background: "#f5f0e8",
    borderRadius: 0,
    border: "3px solid #f5f0e8",
    boxShadow: "8px 8px 0 rgba(0,164,228,0.3), -4px -4px 0 rgba(228,0,124,0.2)",
    padding: PAD,
  },

  // Features
  features: {
    padding: "80px 40px",
    backgroundColor: "#f5f0e8",
    backgroundImage: "radial-gradient(circle, #d6d0c4 0.5px, transparent 0.5px)",
    backgroundSize: "4px 4px",
  },
  sectionTitle: {
    fontSize: 36,
    fontWeight: 700,
    textAlign: "center",
    marginBottom: 48,
    color: "#1a1a1a",
    fontFamily: "'Georgia', serif",
    textTransform: "uppercase",
    letterSpacing: 2,
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 32,
    maxWidth: 1100,
    margin: "0 auto",
  },
  featureCard: {
    background: "#fdfcf9",
    padding: 32,
    borderRadius: 0,
    border: "2px solid #1a1a1a",
    boxShadow: "4px 4px 0 #1a1a1a",
    textAlign: "center",
  },
  featureIcon: {
    width: 56,
    height: 56,
    background: "#1a1a1a",
    color: "#ffe600",
    borderRadius: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    fontWeight: 700,
    margin: "0 auto 20px",
    fontFamily: "monospace",
  },
  featureTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 12,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  featureDesc: {
    fontSize: 15,
    color: "#444",
    lineHeight: 1.7,
  },

  // How It Works
  storyboards: {
    padding: "72px 24px",
    borderTop: "2px solid #1a1a1a",
  },
  storyboardsInner: {
    maxWidth: 1100,
    margin: "0 auto",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 40,
  },
  storyboardsText: { flex: "1 1 340px", minWidth: 0 },
  storyboardsPreview: { flex: "1 1 360px", minWidth: 0 },
  storyboardsDesc: { fontSize: 17, color: "#444", lineHeight: 1.7, margin: "0 0 16px" },
  storyboardsLink: { color: "#00a4e4", textDecoration: "none", fontWeight: 700 },
  howItWorks: {
    padding: "80px 40px",
    background: "#fdfcf9",
  },
  steps: {
    display: "flex",
    justifyContent: "center",
    gap: 48,
    flexWrap: "wrap",
    marginBottom: 48,
  },
  step: {
    maxWidth: 280,
    textAlign: "center",
  },
  stepNumber: {
    width: 48,
    height: 48,
    background: "#00a4e4",
    color: "#fff",
    borderRadius: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 700,
    margin: "0 auto 20px",
    border: "2px solid #1a1a1a",
    fontFamily: "monospace",
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 12,
    color: "#1a1a1a",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  stepDesc: {
    fontSize: 15,
    color: "#444",
    lineHeight: 1.7,
  },
  ctaSection: {
    textAlign: "center",
    paddingTop: 24,
  },
  ctaButtonSecondary: {
    display: "inline-block",
    padding: "14px 36px",
    background: "#1a1a1a",
    color: "#f5f0e8",
    fontSize: 17,
    fontWeight: 700,
    borderRadius: 0,
    textDecoration: "none",
    textTransform: "uppercase",
    letterSpacing: 1,
    border: "2px solid #1a1a1a",
    fontFamily: "'Georgia', serif",
  },
  ctaNote: {
    marginTop: 16,
    fontSize: 14,
    color: "#888",
    fontStyle: "italic",
  },

  // Footer
  footer: {
    padding: "48px 40px 32px",
    backgroundColor: "#1a1a1a",
    backgroundImage: "radial-gradient(circle, rgba(255,230,0,0.06) 1px, transparent 1px)",
    backgroundSize: "6px 6px",
    color: "#f5f0e8",
    borderTop: "3px solid #00a4e4",
  },
  footerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 32,
    maxWidth: 1100,
    margin: "0 auto",
  },
  footerBrand: {},
  footerLogo: {
    fontSize: 28,
    fontWeight: 700,
    fontFamily: "'Georgia', serif",
  },
  footerTagline: {
    fontSize: 14,
    opacity: 0.7,
    marginTop: 8,
    fontStyle: "italic",
  },
  footerNav: {
    display: "flex",
    gap: 24,
    flexWrap: "wrap",
  },
  footerLink: {
    color: "#f5f0e8",
    textDecoration: "none",
    fontSize: 13,
    opacity: 0.8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  copyright: {
    textAlign: "center",
    fontSize: 13,
    opacity: 0.5,
    marginTop: 40,
    fontStyle: "italic",
  },
};

export default LandingPage;
